import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { AiToolTypeValueType } from '@lessonup/analytics';
import {
  IconBookAlt,
  IconComponentImage,
  IconEdit,
  IconEditTemplate,
  IconFileMultiple,
  IconFileText,
  IconGlobe,
  IconLanguageAlt,
  IconLesson,
  IconMicrophone,
  IconSlideTypeQuizQuestion,
  IconTable,
} from '@lessonup/ui-components';
import { lessonGeneratorRoute } from '../../utils/bridge/teacherRoutes';
import { FeatureFlag } from '../../utils/featureFlags/useFeatureFlags';
import { showCreateLessonModal } from '../Explorer/components/CreateLessonModal/CreateLessonModal';
import {
  AiToolboxGenerateAssignmentDocument,
  AiToolboxGenerateDebateTopicsDocument,
  AiToolboxGenerateGlossaryDocument,
  AiToolboxGenerateImageDocument,
  AiToolboxGenerateLessonRecapDocument,
  AiToolboxGenerateQuizDocument,
  AiToolboxGenerateRubricDocument,
  AiToolboxGenerateSummaryDocument,
  AiToolboxGenerateSummaryForWebsiteDocument,
  AiToolboxRewriteTextDocument,
  AiToolboxTranslateTextDocument,
} from './AIToolBox.graphql.generated';

const summaryGenerator = createDefaultAITool({
  type: 'summary',
  slug: 'summary',
  labelKey: 'summaryLabel',
  descriptionKey: 'summaryDescription',
  seoTitleKey: 'summarySeoTitle',
  document: AiToolboxGenerateSummaryDocument,
  icon: IconFileText,
  extractor: createExtractor(AiToolboxGenerateSummaryDocument, (data) => data.aiToolboxGenerateSummary),
});

export type SummaryGenerator = typeof summaryGenerator;

const summaryWebsiteGenerator = createDefaultAITool({
  type: 'summaryWebsite',
  slug: 'summary-website',
  labelKey: 'summaryWebsiteLabel',
  descriptionKey: 'summaryWebsiteDescription',
  seoTitleKey: 'summaryWebsiteSeoTitle',
  document: AiToolboxGenerateSummaryForWebsiteDocument,
  icon: IconGlobe,
  extractor: createExtractor(
    AiToolboxGenerateSummaryForWebsiteDocument,
    (data) => data.aiToolboxGenerateSummaryForWebsite
  ),
});
export type SummaryWebsiteGenerator = typeof summaryWebsiteGenerator;

const translateGenerator = createDefaultAITool({
  type: 'translate',
  slug: 'translate',
  labelKey: 'translateLabel',
  descriptionKey: 'translateDescription',
  seoTitleKey: 'translateSeoTitle',
  document: AiToolboxTranslateTextDocument,
  icon: IconLanguageAlt,
  extractor: createExtractor(AiToolboxTranslateTextDocument, (data) => data.aiToolboxTranslateText),
});
export type TranslateGenerator = typeof translateGenerator;

const rubricGenerator = createDefaultAITool({
  type: 'rubric',
  slug: 'rubric',
  labelKey: 'rubricLabel',
  descriptionKey: 'rubricDescription',
  seoTitleKey: 'rubricSeoTitle',
  document: AiToolboxGenerateRubricDocument,
  icon: IconTable,
  extractor: createExtractor(AiToolboxGenerateRubricDocument, (data) => data.aiToolboxGenerateRubric),
});
export type RubricGenerator = typeof rubricGenerator;

const rewriteGenerator = createDefaultAITool({
  type: 'rewrite',
  slug: 'rewrite',
  labelKey: 'rewriteLabel',
  descriptionKey: 'rewriteDescription',
  seoTitleKey: 'rewriteSeoTitle',
  document: AiToolboxRewriteTextDocument,
  icon: IconEdit,
  extractor: createExtractor(AiToolboxRewriteTextDocument, (data) => data.aiToolboxRewriteText),
});
export type RewriteGenerator = typeof rewriteGenerator;

const debateTopicsGenerator = createDefaultAITool({
  type: 'debateTopics',
  slug: 'debate-topics',
  labelKey: 'debateTopicsLabel',
  descriptionKey: 'debateTopicsDescription',
  seoTitleKey: 'debateTopicsSeoTitle',
  document: AiToolboxGenerateDebateTopicsDocument,
  icon: IconMicrophone,
  extractor: createExtractor(AiToolboxGenerateDebateTopicsDocument, (data) => data.aiToolboxGenerateDebateTopics),
});
export type DebateTopicsGenerator = typeof debateTopicsGenerator;

const glossaryGenerator = createDefaultAITool({
  type: 'glossary',
  slug: 'glossary',
  labelKey: 'glossaryLabel',
  descriptionKey: 'glossaryDescription',
  seoTitleKey: 'glossarySeoTitle',
  document: AiToolboxGenerateGlossaryDocument,
  icon: IconBookAlt,
  extractor: createExtractor(AiToolboxGenerateGlossaryDocument, (data) => data.aiToolboxGenerateGlossary),
});
export type GlossaryGenerator = typeof glossaryGenerator;

const assignmentGenerator = createDefaultAITool({
  type: 'assignment',
  slug: 'assignment',
  labelKey: 'assignmentLabel',
  descriptionKey: 'assignmentDescription',
  seoTitleKey: 'assignmentSeoTitle',
  document: AiToolboxGenerateAssignmentDocument,
  icon: IconEditTemplate,
  extractor: createExtractor(AiToolboxGenerateAssignmentDocument, (data) => data.aiToolboxGenerateAssignment),
});
export type AssignmentGenerator = typeof assignmentGenerator;

const lessonRecapGenerator = createDefaultAITool({
  type: 'lessonRecap',
  slug: 'lesson-recap',
  labelKey: 'lessonRecapLabel',
  descriptionKey: 'lessonRecapDescription',
  seoTitleKey: 'lessonRecapSeoTitle',
  document: AiToolboxGenerateLessonRecapDocument,
  icon: IconFileMultiple,
  extractor: createExtractor(AiToolboxGenerateLessonRecapDocument, (data) => data.aiToolboxGenerateLessonRecap),
});

export type LessonRecapGenerator = typeof lessonRecapGenerator;

const imageGenerator = createDefaultAITool({
  type: 'image',
  slug: 'image',
  labelKey: 'imageLabel',
  descriptionKey: 'imageDescription',
  seoTitleKey: 'generateImageSeoTitle',
  document: AiToolboxGenerateImageDocument,
  icon: IconComponentImage,
  featureFlag: 'imageGenerator',
  extractor: createExtractor(AiToolboxGenerateImageDocument, (data) => ({
    markdown: data.aiToolboxGenerateImage.imageUrl,
  })),
});
export type ImageGenerator = typeof imageGenerator;

const lessonGenerator = createOnClickAITool({
  type: 'lesson',
  labelKey: 'generateLessonLabel',
  descriptionKey: 'generateLessonDescription',
  icon: IconLesson,
  onClick: async () => {
    const result = await showCreateLessonModal({
      preferredStep: 'ai',
    });

    if (result.type === 'maia') {
      window.location.href = lessonGeneratorRoute.href({
        generatorType: result.generatorType,
        source: 'ai-tools',
      });
    }
  },
});
export type LessonGenerator = typeof lessonGenerator;

const quizGenerator = createDefaultAITool({
  type: 'quiz',
  slug: 'quiz',
  labelKey: 'quizLabel',
  descriptionKey: 'quizDescription',
  seoTitleKey: 'quizSeoTitle',
  document: AiToolboxGenerateQuizDocument,
  icon: IconSlideTypeQuizQuestion,
  extractor: createExtractor(AiToolboxGenerateQuizDocument, (data) => ({
    markdown: data.aiToolboxGenerateQuiz.questions.map((question) => question.question).join('\n\n'),
  })),
  featureFlag: undefined,
});

export type QuizGenerator = typeof quizGenerator;

// generators are shown in the following order on the overview page
export const generators = [
  assignmentGenerator,
  rubricGenerator,
  lessonGenerator,
  debateTopicsGenerator,
  glossaryGenerator,
  lessonRecapGenerator,
  imageGenerator,
  summaryWebsiteGenerator,
  summaryGenerator,
  rewriteGenerator,
  translateGenerator,
  quizGenerator,
] as const;

// generators are shown in the following order in the editor pane
export const editorGenerators = [
  summaryGenerator,
  rewriteGenerator,
  translateGenerator,
  imageGenerator,
  debateTopicsGenerator,
  glossaryGenerator,
  lessonRecapGenerator,
] as const;

export type AiToolboxGenerator = (typeof generators)[number];
export type AiToolsType = AiToolboxGenerator['type'];

export type AiToolsStandardGenerator = Extract<AiToolboxGenerator, { action: 'default' }>;
export type AiToolsStandardGeneratorType = AiToolsStandardGenerator['type'];
export type AiToolboxSlug = AiToolsStandardGenerator['slug'];
export type AiToolboxDocument = AiToolsStandardGenerator['document'];

export type AiToolOnClickGenerator = Extract<AiToolboxGenerator, { action: 'onClick' }>;
export type AiToolOnClickGeneratorType = AiToolOnClickGenerator['type'];

type DataExtractor<TData> = (data: TData) => AiToolboxResult;

export interface AiToolboxResult {
  markdown?: string;
  html?: string;
  slideTitle?: string;
}

export const isStandardGenerator = (generator: AiToolboxGenerator): generator is AiToolsStandardGenerator =>
  generator.action === 'default';

export const isOnClickGenerator = (generator: AiToolboxGenerator): generator is AiToolOnClickGenerator =>
  generator.action === 'onClick';

export const aiToolsTypeToEventType: Record<AiToolsStandardGeneratorType, AiToolTypeValueType> = {
  summary: 'text_summarize',
  summaryWebsite: 'website_summarize',
  translate: 'text_translate',
  rubric: 'rubric_create',
  rewrite: 'text_rewrite',
  debateTopics: 'debate_topics_create',
  glossary: 'glossary_create',
  assignment: 'task_create',
  lessonRecap: 'lesson_recap',
  image: 'image_create',
  quiz: 'quiz_create',
};

type IconType = typeof IconFileText;

interface BaseAIToolProps<F extends FeatureFlag | undefined = undefined> {
  labelKey: string;
  descriptionKey: string;
  icon: IconType;
  /** undefined is show */
  featureFlag?: F;
}

// these 'any' types are needed to make the type inference work, but are not unsafe
// they are overridden in the createAITool functions
type DefaultAITool<
  T extends string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  D extends TypedDocumentNode<any, any>,
  F extends FeatureFlag | undefined = undefined,
> = BaseAIToolProps<F> & {
  type: T;
  action: 'default';
  seoTitleKey: string;
  slug: string;
  document: D;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extractor: DataExtractor<D extends TypedDocumentNode<infer TData, any> ? TData : never>;
};

type OnClickAITool<T extends string, F extends FeatureFlag | undefined = undefined> = BaseAIToolProps<F> & {
  type: T;
  action: 'onClick';
  onClick: () => void | Promise<void>;
};

/** creates a default ai tool */
function createDefaultAITool<
  T extends string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  D extends TypedDocumentNode<any, any>,
  F extends FeatureFlag | undefined = undefined,
>(tool: Omit<DefaultAITool<T, D, F>, 'action'>): DefaultAITool<T, D, F> {
  return {
    ...tool,
    action: 'default',
  };
}

/** creates an ai tool that is handled by a click */
function createOnClickAITool<T extends string, F extends FeatureFlag | undefined = undefined>(
  tool: Omit<OnClickAITool<T, F>, 'action'>
): OnClickAITool<T, F> {
  return {
    ...tool,
    action: 'onClick',
  };
}

// Helper functions to typesafe get the data from the subscription
function createExtractor<TData, TVariables>(
  _document: TypedDocumentNode<TData, TVariables>,
  func: DataExtractor<TData>
) {
  return func;
}
